import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';

import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import DocumentSection from './components/DocumentSection';
import AcceptanceModal from './components/AcceptanceModal';
import ComplianceStatus from './components/ComplianceStatus';
import PolicyUpdates from './components/PolicyUpdates';

const LegalComplianceCenter = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showAcceptanceModal, setShowAcceptanceModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const documents = [
    {
      id: 'terms-of-service',
      title: 'Terms of Service',
      category: 'Legal Agreement',
      version: '2.1.0',
      lastUpdated: '2026-01-15',
      complianceStandards: ['HIPAA', 'SOC2', 'GDPR'],
      icon: 'FileText',
      color: 'var(--color-primary)',
      sections: [
        {
          id: 'acceptance',
          title: '1. Acceptance of Terms',
          content: 'By accessing and using FitWellness gym facilities and services, you agree to be bound by these Terms of Service. These terms comply with HIPAA regulations for health information protection, SOC2 standards for data security, and GDPR requirements for data privacy and user rights.'
        },
        {
          id: 'membership',
          title: '2. Membership Agreement',
          content: 'Your membership grants you access to gym facilities, equipment, and services as outlined in your selected plan. All memberships are subject to facility rules, safety guidelines, and applicable health regulations. We maintain SOC2 Type II certification for secure handling of your membership data.'
        },
        {
          id: 'health-data',
          title: '3. Health Information & HIPAA Compliance',
          content: 'We collect and process health-related information in strict accordance with HIPAA regulations. Your workout data, biometric measurements, and health assessments are encrypted and stored securely. You have the right to access, modify, or request deletion of your health information at any time.'
        },
        {
          id: 'liability',
          title: '4. Liability & Risk Acknowledgment',
          content: 'Physical exercise involves inherent risks. By using our facilities, you acknowledge these risks and agree to follow all safety guidelines. We maintain comprehensive insurance coverage and implement safety protocols in compliance with industry standards.'
        },
        {
          id: 'termination',
          title: '5. Termination & Cancellation',
          content: 'You may cancel your membership at any time with 30 days notice. Refunds are processed according to our refund policy. We reserve the right to terminate memberships for violations of facility rules or terms of service.'
        }
      ]
    },
    {
      id: 'privacy-policy',
      title: 'Privacy Policy',
      category: 'Data Protection',
      version: '3.0.2',
      lastUpdated: '2026-01-20',
      complianceStandards: ['HIPAA', 'SOC2', 'GDPR'],
      icon: 'Shield',
      color: 'var(--color-success)',
      sections: [
        {
          id: 'data-collection',
          title: '1. Data Collection & Processing',
          content: 'We collect personal information including name, contact details, payment information, and health data necessary for providing fitness services. All data collection is performed with explicit consent and in compliance with GDPR Article 6 (lawful basis) and HIPAA Privacy Rule requirements.'
        },
        {
          id: 'data-usage',
          title: '2. How We Use Your Data',
          content: 'Your data is used exclusively for: (a) providing fitness services, (b) personalizing workout recommendations, (c) processing payments, (d) communicating important updates, and (e) improving our services. We never sell your personal information to third parties.'
        },
        {
          id: 'data-security',
          title: '3. Data Security & SOC2 Compliance',
          content: 'We implement industry-leading security measures including AES-256 encryption, secure data centers, regular security audits, and access controls. Our SOC2 Type II certification demonstrates our commitment to maintaining the highest security standards for your data.'
        },
        {
          id: 'user-rights',
          title: '4. Your Rights Under GDPR',
          content: 'You have the right to: (a) access your personal data, (b) rectify inaccurate data, (c) request data deletion (right to be forgotten), (d) data portability, (e) restrict processing, (f) object to processing, and (g) withdraw consent at any time. Contact our Data Protection Officer to exercise these rights.'
        },
        {
          id: 'data-retention',
          title: '5. Data Retention & Deletion',
          content: 'We retain your data only as long as necessary for service provision and legal compliance. Health data is retained for 7 years per HIPAA requirements. Upon account deletion, personal data is permanently removed within 30 days, except where legal retention is required.'
        },
        {
          id: 'international-transfers',
          title: '6. International Data Transfers',
          content: 'If data is transferred outside your jurisdiction, we ensure adequate protection through Standard Contractual Clauses (SCCs) approved by the European Commission and implement additional safeguards as required by GDPR Chapter V.'
        }
      ]
    },
    {
      id: 'data-protection-notice',
      title: 'Data Protection Notice',
      category: 'GDPR Compliance',
      version: '1.5.0',
      lastUpdated: '2026-01-18',
      complianceStandards: ['GDPR'],
      icon: 'Lock',
      color: 'var(--color-warning)',
      sections: [
        {
          id: 'controller',
          title: '1. Data Controller Information',
          content: 'FitWellness Inc. acts as the data controller for your personal information. Our Data Protection Officer can be reached at dpo@fitwellness.com. We are registered with relevant data protection authorities and comply with all GDPR obligations.'
        },
        {
          id: 'legal-basis',
          title: '2. Legal Basis for Processing',
          content: 'We process your data based on: (a) contractual necessity for membership services, (b) legal obligations for health and safety compliance, (c) legitimate interests for service improvement, and (d) explicit consent for marketing communications.'
        },
        {
          id: 'automated-decisions',
          title: '3. Automated Decision-Making',
          content: 'We use automated systems for workout recommendations and personalized fitness plans. You have the right to request human review of any automated decisions that significantly affect you. Our AI systems are designed with fairness and transparency principles.'
        },
        {
          id: 'breach-notification',
          title: '4. Data Breach Procedures',
          content: 'In the event of a data breach affecting your personal information, we will notify you within 72 hours as required by GDPR Article 33. Notifications include the nature of the breach, potential consequences, and measures taken to address it.'
        }
      ]
    },
    {
      id: 'health-information-handling',
      title: 'Health Information Handling',
      category: 'HIPAA Compliance',
      version: '2.3.1',
      lastUpdated: '2026-01-22',
      complianceStandards: ['HIPAA'],
      icon: 'Heart',
      color: 'var(--color-error)',
      sections: [
        {
          id: 'phi-definition',
          title: '1. Protected Health Information (PHI)',
          content: 'PHI includes any health information that can identify you, including workout data, biometric measurements, injury reports, and health assessments. We treat all PHI with the highest level of security and confidentiality as required by HIPAA.'
        },
        {
          id: 'minimum-necessary',
          title: '2. Minimum Necessary Standard',
          content: 'We adhere to the HIPAA minimum necessary standard, accessing and using only the minimum amount of PHI required to accomplish the intended purpose. Staff access to health information is role-based and strictly controlled.'
        },
        {
          id: 'business-associates',
          title: '3. Business Associate Agreements',
          content: 'All third-party service providers who may access PHI have signed Business Associate Agreements (BAAs) ensuring HIPAA compliance. This includes payment processors, cloud storage providers, and fitness equipment vendors.'
        },
        {
          id: 'patient-rights',
          title: '4. Your Rights to Health Information',
          content: 'Under HIPAA, you have the right to: (a) access your health records, (b) request corrections, (c) receive an accounting of disclosures, (d) request restrictions on uses and disclosures, (e) request confidential communications, and (f) file a complaint if you believe your rights have been violated.'
        },
        {
          id: 'security-measures',
          title: '5. Technical & Physical Safeguards',
          content: 'We implement HIPAA-required safeguards including: encrypted data transmission and storage, secure facility access controls, regular security risk assessments, employee training programs, and incident response procedures.'
        }
      ]
    }
  ];

  const filteredDocuments = documents?.filter(doc =>
    doc?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    doc?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    doc?.complianceStandards?.some(standard => 
      standard?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )
  );

  const toggleSection = (docId, sectionId) => {
    const key = `${docId}-${sectionId}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const handleAcceptDocument = (document) => {
    setSelectedDocument(document);
    setShowAcceptanceModal(true);
  };

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Legal Compliance Center</h1>
              <p className="text-muted-foreground">HIPAA, SOC2, and GDPR compliant legal documentation</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e?.target?.value)}
                className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {languages?.map(lang => (
                  <option key={lang?.code} value={lang?.code}>{lang?.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Icon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
            <Input
              type="text"
              placeholder="Search documents, compliance standards, or topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="pl-12"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Documents */}
          <div className="lg:col-span-2 space-y-6">
            {filteredDocuments?.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="FileSearch" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No documents found</h3>
                <p className="text-muted-foreground">Try adjusting your search terms</p>
              </div>
            ) : (
              filteredDocuments?.map(document => (
                <div key={document?.id}>
                  {document?.sections?.map(section => (
                    <DocumentSection
                      key={`${document?.id}-${section?.id}`}
                      document={document}
                      documentId={document?.id}
                      section={section}
                      isExpanded={expandedSections?.[`${document?.id}-${section?.id}`] || false}
                      onToggle={() => toggleSection(document?.id, section?.id)}
                      expandedSections={expandedSections}
                      onToggleSection={toggleSection}
                      onAccept={handleAcceptDocument}
                    />
                  ))}
                </div>
              ))
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <ComplianceStatus standards={['HIPAA', 'SOC2', 'GDPR']} />
            <PolicyUpdates />
          </div>
        </div>
      </main>

      <MobileBottomNav />

      {showAcceptanceModal && selectedDocument && (
        <AcceptanceModal
          document={selectedDocument}
          onClose={() => {
            setShowAcceptanceModal(false);
            setSelectedDocument(null);
          }}
          onAccept={() => {
            setShowAcceptanceModal(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};

export default LegalComplianceCenter;